/**
 * MessageBubble Component
 *
 * Renders a single chat message. Handles user, assistant, and tool messages
 * with appropriate styling. Assistant messages support markdown and portal links.
 *
 * Each user and assistant message has a copy-to-clipboard button that appears
 * on hover (desktop) or on tap of the message area (mobile).
 */

import React, { useState, useCallback } from 'react';
import { Tag, Tooltip } from 'antd';
import {
  ToolOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UserOutlined,
  RobotOutlined,
  CopyOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import { Link } from 'react-router-dom';

interface MessageBubbleProps {
  role: string;
  content: string | null;
  toolName?: string | null;
  toolResult?: Record<string, unknown> | null;
  isStreaming?: boolean;
}

const MarkdownWithLinks: React.FC<{ content: string }> = ({ content }) => (
  <ReactMarkdown
    components={{
      a: ({ href, children }) => {
        if (href?.startsWith('/')) {
          return <Link to={href} style={{ color: '#1677ff', fontWeight: 500 }}>{children}</Link>;
        }
        return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
      },
      table: ({ children }) => (
        <table style={{ borderCollapse: 'collapse', width: '100%', margin: '8px 0', fontSize: 12, whiteSpace: 'nowrap' }}>
          {children}
        </table>
      ),
      th: ({ children }) => (
        <th style={{ borderBottom: '1px solid #e8e8e8', padding: '3px 6px', textAlign: 'left', fontWeight: 600 }}>
          {children}
        </th>
      ),
      td: ({ children }) => (
        <td style={{ borderBottom: '1px solid #f0f0f0', padding: '3px 6px' }}>
          {children}
        </td>
      ),
    }}
  >
    {content}
  </ReactMarkdown>
);

const CopyButton: React.FC<{ text: string; isUser: boolean }> = ({ text, isUser }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  }, [text]);

  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy'} placement={isUser ? 'left' : 'right'}>
      <button
        onClick={handleCopy}
        className="chat-copy-btn"
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 2,
          borderRadius: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: isUser ? 'rgba(255,255,255,0.7)' : '#8c8c8c',
          transition: 'color 0.2s',
        }}
        aria-label="Copy message"
      >
        {copied
          ? <CheckOutlined style={{ fontSize: 12 }} />
          : <CopyOutlined style={{ fontSize: 12 }} />
        }
      </button>
    </Tooltip>
  );
};

const MessageBubble: React.FC<MessageBubbleProps> = ({
  role,
  content,
  toolName,
  toolResult,
  isStreaming,
}) => {
  if (role === 'tool_use') {
    return (
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '4px 0', marginLeft: 32 }}>
        <ToolOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
        <Tag color="processing" style={{ margin: 0, fontSize: 12 }}>
          {toolName}
        </Tag>
      </div>
    );
  }

  if (role === 'tool_result') {
    const success = toolResult?.success !== false;
    return (
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '2px 0', marginLeft: 32 }}>
        {success
          ? <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 12 }} />
          : <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 12 }} />
        }
        <span style={{ fontSize: 12, color: '#8c8c8c' }}>
          {success ? 'Completed' : String(toolResult?.error || 'Failed')}
        </span>
      </div>
    );
  }

  const isUser = role === 'user';
  const textContent = content || '';

  return (
    <div
      className="chat-message-row"
      style={{
        display: 'flex',
        flexDirection: isUser ? 'row-reverse' : 'row',
        gap: 8,
        alignItems: 'flex-start',
        marginBottom: 12,
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          background: isUser ? '#1677ff' : '#f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {isUser
          ? <UserOutlined style={{ color: '#fff', fontSize: 14 }} />
          : <RobotOutlined style={{ color: '#595959', fontSize: 14 }} />
        }
      </div>
      <div style={{ maxWidth: isUser ? '80%' : '92%' }}>
        <div
          style={{
            padding: '8px 12px',
            borderRadius: isUser ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
            background: isUser ? '#1677ff' : '#f5f5f5',
            color: isUser ? '#fff' : '#262626',
            fontSize: 14,
            lineHeight: 1.6,
            wordBreak: 'break-word',
          }}
        >
          {isUser ? (
            <span>{content}</span>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <MarkdownWithLinks content={textContent} />
              {isStreaming && (
                <span
                  style={{
                    display: 'inline-block',
                    width: 6,
                    height: 16,
                    background: '#595959',
                    marginLeft: 2,
                    animation: 'blink 1s step-end infinite',
                    verticalAlign: 'text-bottom',
                  }}
                />
              )}
            </div>
          )}
        </div>
        {textContent && !isStreaming && (
          <div
            className="chat-copy-wrapper"
            style={{
              display: 'flex',
              justifyContent: isUser ? 'flex-end' : 'flex-start',
              marginTop: 2,
              opacity: 0,
              transition: 'opacity 0.15s',
            }}
          >
            <CopyButton text={textContent} isUser={isUser} />
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageBubble;
